import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { db, storage } from '../api/firebase';
import { collection, addDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

const FormPage = () => {
    const { register, handleSubmit, formState: { errors } } = useForm();

    // Handle form submission
    const onSubmit = async (data) => {
        try {
            let fileUrl = null;
            if (data.file[0]) {
                const file = data.file[0];
                const storageRef = ref(storage, `form/${file.name}`);
                const snapshot = await uploadBytes(storageRef, file);
                fileUrl = await getDownloadURL(snapshot.ref);
            }
            // Add form data to Firestore
            const docRef = await addDoc(collection(db, 'form'), {
                fullName: data.fullName,
                email: data.email,
                phone: data.phone,
                documentType: data.documentType,
                wordCount: data.wordCount,
                subjectArea: data.subjectArea,
                targetAudience: data.targetAudience,
                desiredService: data.desiredService,
                deadline: data.deadline,
                instructions: data.instructions,
                fileUrl: fileUrl,
                timestamp: new Date(), // Add a timestamp
            });

            console.log('Document written with ID: ', docRef.id);
            alert('Form submitted successfully!');
        } catch (error) {
            console.error('Error adding document: ', error);
            alert('An error occurred while submitting the form.');
        }
    };

    return (
        <>
            <nav className="bg-blue-600 text-white py-4 px-8 shadow-md">
                <div className="container mx-auto flex justify-between items-center">
                    <RouterLink
                        to="/"
                        className="text-white text-sm font-bold leading-relaxed inline-block mr-4 py-2 whitespace-nowrap uppercase"
                    >
                        Sofia
                    </RouterLink>
                </div>
            </nav>

            <div className="container mx-auto p-8" id="privacy">
                <h1 className="text-2xl font-bold mb-6">Submit Your Project</h1>

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                    {/* Contact Information */}
                    <div>
                        <h2 className="text-xl font-semibold mb-4">1. Contact Information</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Full Name</label>
                                <input
                                    type="text"
                                    {...register('fullName', { required: 'Full Name is required' })}
                                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                />
                                {errors.fullName && <p className="text-red-500 text-sm mt-1">{errors.fullName.message}</p>}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Email Address</label>
                                <input
                                    type="email"
                                    {...register('email', { required: 'Email is required', pattern: { value: /^\S+@\S+$/i, message: 'Invalid email address' } })}
                                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                />
                                {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Phone Number</label>
                                <input
                                    type="tel"
                                    {...register('phone', { pattern: { value: /^[0-9]{10}$/, message: 'Invalid phone number' } })}
                                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                />
                                {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone.message}</p>}
                            </div>
                        </div>
                    </div>

                    {/* Project Details */}
                    <div>
                        <h2 className="text-xl font-semibold mb-4">2. Project Details</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Document Type</label>
                                <select
                                    {...register('documentType', { required: 'Document Type is required' })}
                                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                >
                                    <option value="">Select Document Type</option>
                                    <option value="manuscript">Manuscript</option>
                                    <option value="book-chapter">Book Chapter</option>
                                    <option value="academic-paper">Academic Paper</option>
                                    <option value="thesis">Thesis</option>
                                    <option value="website-content">Website Content</option>
                                    <option value="business-report">Business Report</option>
                                    <option value="cv-resume">CV/Resume</option>
                                </select>
                                {errors.documentType && <p className="text-red-500 text-sm mt-1">{errors.documentType.message}</p>}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Word Count</label>
                                <input
                                    type="number"
                                    {...register('wordCount', { required: 'Word Count is required', min: { value: 1, message: 'Word Count must be at least 1' } })}
                                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                />
                                {errors.wordCount && <p className="text-red-500 text-sm mt-1">{errors.wordCount.message}</p>}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Subject Area/Field</label>
                                <input
                                    type="text"
                                    {...register('subjectArea', { required: 'Subject Area is required' })}
                                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                />
                                {errors.subjectArea && <p className="text-red-500 text-sm mt-1">{errors.subjectArea.message}</p>}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Target Audience</label>
                                <input
                                    type="text"
                                    {...register('targetAudience', { required: 'Target Audience is required' })}
                                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                />
                                {errors.targetAudience && <p className="text-red-500 text-sm mt-1">{errors.targetAudience.message}</p>}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Desired Service</label>
                                <select
                                    {...register('desiredService', { required: 'Desired Service is required' })}
                                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                >
                                    <option value="">Select Service</option>
                                    <option value="proofreading">Proofreading</option>
                                    <option value="copy-editing">Copy Editing</option>
                                    <option value="substantive-editing">Substantive Editing</option>
                                    <option value="developmental-editing">Developmental Editing</option>
                                </select>
                                {errors.desiredService && <p className="text-red-500 text-sm mt-1">{errors.desiredService.message}</p>}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Deadline</label>
                                <input
                                    type="date"
                                    {...register('deadline', { required: 'Deadline is required' })}
                                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                />
                                {errors.deadline && <p className="text-red-500 text-sm mt-1">{errors.deadline.message}</p>}
                            </div>
                            <div className="col-span-2">
                                <label className="block text-sm font-medium text-gray-700">Specific Instructions or Preferences</label>
                                <textarea
                                    {...register('instructions')}
                                    rows="4"
                                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                ></textarea>
                            </div>
                        </div>
                    </div>

                    {/* File Upload Section */}
                    <div>
                        <h2 className="text-xl font-semibold mb-4">3. Upload Your File</h2>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Upload File (Word, PDF, or PowerPoint)</label>
                            <input
                                type="file"
                                {...register('file', {
                                    required: 'File is required',
                                    validate: {
                                        validFileType: (value) => {
                                            if (!value[0]) return true; // Skip validation if no file is selected
                                            const allowedTypes = [
                                                'application/msword',
                                                'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
                                                'application/pdf',
                                                'application/vnd.ms-powerpoint',
                                                'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx
                                            ];
                                            return allowedTypes.includes(value[0].type) || 'Invalid file type';
                                        },
                                        validFileSize: (value) => {
                                            if (!value[0]) return true; // Skip validation if no file is selected
                                            return value[0].size <= 5 * 1024 * 1024 || 'File size must be less than 5MB';
                                        },
                                    },
                                })}
                                accept=".doc,.docx,.pdf,.ppt,.pptx"
                                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                            />
                            {errors.file && <p className="text-red-500 text-sm mt-1">{errors.file.message}</p>}
                            <p className="text-sm text-gray-500 mt-2">Accepted file types: .doc, .docx, .pdf, .ppt, .pptx (Max size: 5MB)</p>
                        </div>
                    </div>

                    {/* Submit Button */}
                    <div>
                        <button
                            type="submit"
                            style={{
                                backgroundColor: '#3498db', // blue color
                                border: '1px solid #2980b9', // darker blue border
                                color: '#ffffff', // white text
                            }}
                            className="font-semibold py-3 px-6 rounded-lg shadow-md transition duration-300 ease-in-out transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >                        
                            Submit
                        </button>
                    </div>
                </form>
            </div>
        </>
    );
};

export default FormPage;
// import React from 'react';
// import { Link as RouterLink } from 'react-router-dom';
// import { useForm } from 'react-hook-form';
// import { db, storage } from '../api/firebase';
// import { collection, addDoc } from 'firebase/firestore';
// import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

// const FormPage = () => {
//     const { register, handleSubmit } = useForm();

//     // Handle form submission
//     const onSubmit = async (data) => {
//         try {
//             let fileUrl = null;
//             if (data.file[0]) {
//                 const file = data.file[0];
//                 const storageRef = ref(storage, `form/${file.name}`);
//                 const snapshot = await uploadBytes(storageRef, file);
//                 fileUrl = await getDownloadURL(snapshot.ref);
//             }
//             // Add form data to Firestore
//             const docRef = await addDoc(collection(db, 'form'), {
//                 fullName: data.fullName,
//                 email: data.email,
//                 phone: data.phone,
//                 documentType: data.documentType,
//                 wordCount: data.wordCount,
//                 subjectArea: data.subjectArea,
//                 targetAudience: data.targetAudience,
//                 desiredService: data.desiredService,
//                 deadline: data.deadline,
//                 instructions: data.instructions,
//                 fileUrl: fileUrl,
//                 timestamp: new Date(), // Add a timestamp
//             });

//             console.log('Document written with ID: ', docRef.id);
//             alert('Form submitted successfully!');
//         } catch (error) {
//             console.error('Error adding document: ', error);
//             alert('An error occurred while submitting the form.');
//         }
//     };

//     return (
//         <>
//             <nav className="bg-blue-600 text-white py-4 px-8 shadow-md">
//                 <div className="container mx-auto flex justify-between items-center">
//                     <RouterLink
//                         to="/"
//                         className="text-white text-sm font-bold leading-relaxed inline-block mr-4 py-2 whitespace-nowrap uppercase"
//                     >
//                         Sofia
//                     </RouterLink>
//                 </div>
//             </nav>

//             <div className="container mx-auto p-8" id="privacy">
//                 <h1 className="text-2xl font-bold mb-6">Submit Your Project</h1>

//                 <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
//                     {/* Contact Information */}
//                     <div>
//                         <h2 className="text-xl font-semibold mb-4">1. Contact Information</h2>
//                         <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                             <div>
//                                 <label className="block text-sm font-medium text-gray-700">Full Name</label>
//                                 <input
//                                     type="text"
//                                     {...register('fullName', { required: true })}
//                                     className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
//                                 />
//                             </div>
//                             <div>
//                                 <label className="block text-sm font-medium text-gray-700">Email Address</label>
//                                 <input
//                                     type="email"
//                                     {...register('email', { required: true })}
//                                     className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
//                                 />
//                             </div>
//                             <div>
//                                 <label className="block text-sm font-medium text-gray-700">Phone Number</label>
//                                 <input
//                                     type="tel"
//                                     {...register('phone')}
//                                     className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
//                                 />
//                             </div>
//                         </div>
//                     </div>

//                     {/* Project Details */}
//                     <div>
//                         <h2 className="text-xl font-semibold mb-4">2. Project Details</h2>
//                         <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                             <div>
//                                 <label className="block text-sm font-medium text-gray-700">Document Type</label>
//                                 <select
//                                     {...register('documentType', { required: true })}
//                                     className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
//                                 >
//                                     <option value="">Select Document Type</option>
//                                     <option value="manuscript">Manuscript</option>
//                                     <option value="book-chapter">Book Chapter</option>
//                                     <option value="academic-paper">Academic Paper</option>
//                                     <option value="thesis">Thesis</option>
//                                     <option value="website-content">Website Content</option>
//                                     <option value="business-report">Business Report</option>
//                                     <option value="cv-resume">CV/Resume</option>
//                                 </select>
//                             </div>
//                             <div>
//                                 <label className="block text-sm font-medium text-gray-700">Word Count</label>
//                                 <input
//                                     type="number"
//                                     {...register('wordCount', { required: true })}
//                                     className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
//                                 />
//                             </div>
//                             <div>
//                                 <label className="block text-sm font-medium text-gray-700">Subject Area/Field</label>
//                                 <input
//                                     type="text"
//                                     {...register('subjectArea', { required: true })}
//                                     className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
//                                 />
//                             </div>
//                             <div>
//                                 <label className="block text-sm font-medium text-gray-700">Target Audience</label>
//                                 <input
//                                     type="text"
//                                     {...register('targetAudience', { required: true })}
//                                     className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
//                                 />
//                             </div>
//                             <div>
//                                 <label className="block text-sm font-medium text-gray-700">Desired Service</label>
//                                 <select
//                                     {...register('desiredService', { required: true })}
//                                     className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
//                                 >
//                                     <option value="">Select Service</option>
//                                     <option value="proofreading">Proofreading</option>
//                                     <option value="copy-editing">Copy Editing</option>
//                                     <option value="substantive-editing">Substantive Editing</option>
//                                     <option value="developmental-editing">Developmental Editing</option>
//                                 </select>
//                             </div>
//                             <div>
//                                 <label className="block text-sm font-medium text-gray-700">Deadline</label>
//                                 <input
//                                     type="date"
//                                     {...register('deadline', { required: true })}
//                                     className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
//                                 />
//                             </div>
//                             <div className="col-span-2">
//                                 <label className="block text-sm font-medium text-gray-700">Specific Instructions or Preferences</label>
//                                 <textarea
//                                     {...register('instructions')}
//                                     rows="4"
//                                     className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
//                                 ></textarea>
//                             </div>
//                         </div>
//                     </div>

//                     {/* File Upload Section */}
//                     <div>
//                         <h2 className="text-xl font-semibold mb-4">3. Upload Your File</h2>
//                         <div>
//                             <label className="block text-sm font-medium text-gray-700">Upload File (Word, PDF, or PowerPoint)</label>
//                             <input
//                                 type="file"
//                                 {...register('file', {
//                                     required: true,
//                                     validate: {
//                                         validFileType: (value) => {
//                                             if (!value[0]) return true; // Skip validation if no file is selected
//                                             const allowedTypes = [
//                                                 'application/msword',
//                                                 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
//                                                 'application/pdf',
//                                                 'application/vnd.ms-powerpoint',
//                                                 'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx
//                                             ];
//                                             return allowedTypes.includes(value[0].type) || 'Invalid file type';
//                                         },
//                                         validFileSize: (value) => {
//                                             if (!value[0]) return true; // Skip validation if no file is selected
//                                             return value[0].size <= 5 * 1024 * 1024 || 'File size must be less than 5MB';
//                                         },
//                                     },
//                                 })}
//                                 accept=".doc,.docx,.pdf,.ppt,.pptx"
//                                 className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
//                             />
//                             <p className="text-sm text-gray-500 mt-2">Accepted file types: .doc, .docx, .pdf, .ppt, .pptx (Max size: 5MB)</p>
//                         </div>
//                     </div>

//                     {/* Submit Button */}
//                     <div>
//                         <button
//                             type="submit"
//                             style={{
//                                 backgroundColor: '#3498db', // blue color
//                                 border: '1px solid #2980b9', // darker blue border
//                                 color: '#ffffff', // white text
//                             }}
//                             className="font-semibold py-3 px-6 rounded-lg shadow-md transition duration-300 ease-in-out transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
//                         >                        
//                             Submit
//                         </button>
//                     </div>
//                 </form>
//             </div>
//         </>
//     );
// };

// export default FormPage;


///


// import React from 'react';
// import { Link as RouterLink } from 'react-router-dom';
// import { useForm } from 'react-hook-form';

// // Corrected component name to start with an uppercase letter
// const FormPage = () => {
//     const { register, handleSubmit } = useForm();

//     // Handle form submission
//     const onSubmit = (data) => {
//         console.log('Form Data:', data);
//         alert('Form submitted successfully!');
//     };

//     return (
//         <>
//             <nav className="bg-blue-600 text-white py-4 px-8 shadow-md">
//                 <div className="container mx-auto flex justify-between items-center">
//                     <RouterLink
//                         to="/"
//                         className="text-white text-sm font-bold leading-relaxed inline-block mr-4 py-2 whitespace-nowrap uppercase"
//                     >
//                         Sofia
//                     </RouterLink>
//                 </div>
//             </nav>

//             <div className="container mx-auto p-8" id="privacy">
//                 <h1 className="text-2xl font-bold mb-6">Submit Your Project</h1>

//                 <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
//                     {/* Contact Information */}
//                     <div>
//                         <h2 className="text-xl font-semibold mb-4">1. Contact Information</h2>
//                         <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                             <div>
//                                 <label className="block text-sm font-medium text-gray-700">Full Name</label>
//                                 <input
//                                     type="text"
//                                     {...register('fullName', { required: true })}
//                                     className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
//                                 />
//                             </div>
//                             <div>
//                                 <label className="block text-sm font-medium text-gray-700">Email Address</label>
//                                 <input
//                                     type="email"
//                                     {...register('email', { required: true })}
//                                     className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
//                                 />
//                             </div>
//                             <div>
//                                 <label className="block text-sm font-medium text-gray-700">Phone Number</label>
//                                 <input
//                                     type="tel"
//                                     {...register('phone')}
//                                     className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
//                                 />
//                             </div>
//                         </div>
//                     </div>

//                     {/* Project Details */}
//                     <div>
//                         <h2 className="text-xl font-semibold mb-4">2. Project Details</h2>
//                         <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                             <div>
//                                 <label className="block text-sm font-medium text-gray-700">Document Type</label>
//                                 <select
//                                     {...register('documentType', { required: true })}
//                                     className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
//                                 >
//                                     <option value="">Select Document Type</option>
//                                     <option value="manuscript">Manuscript</option>
//                                     <option value="book-chapter">Book Chapter</option>
//                                     <option value="academic-paper">Academic Paper</option>
//                                     <option value="thesis">Thesis</option>
//                                     <option value="website-content">Website Content</option>
//                                     <option value="business-report">Business Report</option>
//                                     <option value="cv-resume">CV/Resume</option>
//                                 </select>
//                             </div>
//                             <div>
//                                 <label className="block text-sm font-medium text-gray-700">Word Count</label>
//                                 <input
//                                     type="number"
//                                     {...register('wordCount', { required: true })}
//                                     className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
//                                 />
//                             </div>
//                             <div>
//                                 <label className="block text-sm font-medium text-gray-700">Subject Area/Field</label>
//                                 <input
//                                     type="text"
//                                     {...register('subjectArea', { required: true })}
//                                     className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
//                                 />
//                             </div>
//                             <div>
//                                 <label className="block text-sm font-medium text-gray-700">Target Audience</label>
//                                 <input
//                                     type="text"
//                                     {...register('targetAudience', { required: true })}
//                                     className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
//                                 />
//                             </div>
//                             <div>
//                                 <label className="block text-sm font-medium text-gray-700">Desired Service</label>
//                                 <select
//                                     {...register('desiredService', { required: true })}
//                                     className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
//                                 >
//                                     <option value="">Select Service</option>
//                                     <option value="proofreading">Proofreading</option>
//                                     <option value="copy-editing">Copy Editing</option>
//                                     <option value="substantive-editing">Substantive Editing</option>
//                                     <option value="developmental-editing">Developmental Editing</option>
//                                 </select>
//                             </div>
//                             <div>
//                                 <label className="block text-sm font-medium text-gray-700">Deadline</label>
//                                 <input
//                                     type="date"
//                                     {...register('deadline', { required: true })}
//                                     className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
//                                 />
//                             </div>
//                             <div className="col-span-2">
//                                 <label className="block text-sm font-medium text-gray-700">Specific Instructions or Preferences</label>
//                                 <textarea
//                                     {...register('instructions')}
//                                     rows="4"
//                                     className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
//                                 ></textarea>
//                             </div>
//                         </div>
//                     </div>

//                     {/* File Upload */}

//                     {/* Submit Button */}
//                     <div>
//                         <button
//                             type="submit"
//                             style={{
//                                 backgroundColor: '#3498db', // blue color
//                                 border: '1px solid #2980b9', // darker blue border
//                                 color: '#ffffff', // white text
//                             }}
//                             className="font-semibold py-3 px-6 rounded-lg shadow-md transition duration-300 ease-in-out transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
//                         >                        
//                             Submit
//                         </button>
//                     </div>
//                 </form>
//             </div>
//         </>
//     );
// };

// export default FormPage;